import { useField } from "formik";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import dayjs from "dayjs";
import { GenericField, InputProps } from "../Fields/GenericField";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type DateBadgeInputProps = InputProps & {
  placeholder?: string;
  min?: string;
  max?: string;
};

export function DateBadgeInput({
  placeholder = "Click to add dates",
  min,
  max,
  ...props
}: DateBadgeInputProps) {
  const [field, meta, helpers] = useField<string[]>(props.name);
  const [isOpen, setIsOpen] = useState(false);

  const values = field.value ?? [];
  const hasError = meta.touched && meta.error;

  function addValue(date: Date) {
    const formatted = dayjs(date).format("YYYY-MM-DD");

    if (!values.includes(formatted)) {
      helpers.setValue([...values, formatted].sort());
    }
  }

  function removeValue(valueToRemove: string) {
    const newValues = values.filter((v) => v !== valueToRemove);
    helpers.setValue(newValues);
  }

  const sortedValues = [...values].sort();

  const today = dayjs().format("YYYY-MM-DD");
  const firstSelectableDate = min && min > today ? min : today;

  const viewNode =
    sortedValues.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {sortedValues.map((value) => (
          <span
            key={value}
            className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-sm font-medium text-slate-700"
          >
            {dayjs(value).format("DD MMM YYYY")}
          </span>
        ))}
      </div>
    ) : (
      <span>--</span>
    );

  return (
    <GenericField viewNode={viewNode} {...props}>
      {() => (
        <span
          className={`relative block w-full before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow-sm after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500 dark:before:hidden ${
            hasError ? "before:shadow-red-500/10" : ""
          }`}
        >
          <div
            className={`relative flex min-h-[calc(2.5rem+2px)] w-full appearance-none flex-wrap items-center gap-2 rounded-lg border px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:min-h-[calc(2rem+2px)] sm:px-[calc(theme(spacing[3])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)] ${
              hasError
                ? "border-red-500 hover:border-red-500 dark:border-red-500 dark:hover:border-red-500"
                : "border-zinc-950/10 hover:border-zinc-950/20 dark:border-white/10 dark:hover:border-white/20"
            } focus-within:outline-hidden bg-transparent dark:bg-white/5`}
          >
            {sortedValues.map((value) => (
              <span
                key={value}
                className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-sm font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-400"
              >
                {dayjs(value).format("DD MMM YYYY")}
                <button
                  type="button"
                  onClick={() => removeValue(value)}
                  className="rounded-sm hover:bg-blue-100 dark:hover:bg-blue-500/20"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </span>
            ))}

            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex min-w-[120px] flex-1 items-center gap-2 border-none bg-transparent p-0 text-base/6 text-zinc-500 outline-none hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm/6 dark:text-zinc-400 dark:hover:text-white"
                >
                  <CalendarIcon className="h-4 w-4" />
                  {values.length === 0 ? placeholder : ""}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  defaultMonth={dayjs(firstSelectableDate).toDate()}
                  disabled={(date) => {
                    const formatted = dayjs(date).format("YYYY-MM-DD");
                    const isPast = date < dayjs().startOf("day").toDate();
                    const isSelected = values.includes(formatted);
                    const isOutOfRange =
                      (!!min && formatted < min) || (!!max && formatted > max);
                    return isPast || isSelected || isOutOfRange;
                  }}
                  modifiers={{
                    selected: (date) =>
                      values.includes(dayjs(date).format("YYYY-MM-DD")),
                  }}
                  onDayClick={(date) => {
                    if (date) {
                      addValue(date);
                    }
                  }}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </span>
      )}
    </GenericField>
  );
}
